"use client";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
} from "recharts";
import { TrendingUp, Globe, Monitor, Clock, Calendar } from "lucide-react";
import { useTranslation } from "react-i18next";

// Chart color palette
const CHART_COLORS = {
  primary: "#6c9956",
  secondary: "#8B5CF6",
  accent: "#06B6D4",
  success: "#10B981",
  warning: "#F59E0B",
};

const PIE_COLORS = [
  CHART_COLORS.primary,
  CHART_COLORS.secondary,
  CHART_COLORS.accent,
  CHART_COLORS.success,
  CHART_COLORS.warning,
];

// Views Over Time Chart
interface ViewsOverTimeProps {
  data: Array<{ date: string; views: number; uniqueVisitors: number }>;
  isLoading?: boolean;
}

export function ViewsOverTimeChart({ data, isLoading }: ViewsOverTimeProps) {
  const { t } = useTranslation("dashboard");

  if (isLoading) {
    return (
      <Card className="overflow-hidden">
        <CardHeader className="pb-4">
          <div className="flex items-center gap-2">
            <Skeleton className="h-8 w-8 rounded-lg" />
            <div className="space-y-2">
              <Skeleton className="h-5 w-32" />
              <Skeleton className="h-4 w-48" />
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="relative">
            <Skeleton className="h-80 w-full rounded-lg" />
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 pt-4 border-t border-border/50">
            {Array.from({ length: 5 }).map((_, i) => (
              <div
                key={i}
                className="text-center p-3 rounded-lg bg-card/50 border border-border/50"
              >
                <Skeleton className="h-6 w-12 mx-auto mb-2" />
                <Skeleton className="h-3 w-16 mx-auto" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  // Validate and clean data
  const validData = Array.isArray(data)
    ? data.filter(
        (item) =>
          item &&
          typeof item.views === "number" &&
          typeof item.uniqueVisitors === "number" &&
          !isNaN(item.views) &&
          !isNaN(item.uniqueVisitors)
      )
    : [];

  // Show empty state if no valid data (but not loading)
  if (validData.length === 0) {
    return (
      <Card className="overflow-hidden">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2">
            <div className="p-2 rounded-lg bg-green-50 dark:bg-green-900/20">
              <TrendingUp className="h-5 w-5 text-green-600 dark:text-green-400" />
            </div>
            {t("charts.viewsOverTime.title")}
          </CardTitle>
          <CardDescription>
            {t("charts.viewsOverTime.description")}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex items-center justify-center h-64">
          <div className="text-center">
            <TrendingUp className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
            <p className="text-muted-foreground">
              {t("charts.viewsOverTime.noData")}
            </p>
            <p className="text-sm text-muted-foreground">
              {t("charts.viewsOverTime.noDataDescription")}
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Calculate summary stats with validation
  const totalViews = validData.reduce(
    (sum, item) => sum + (item.views || 0),
    0
  );
  const totalUniqueVisitors = validData.reduce(
    (sum, item) => sum + (item.uniqueVisitors || 0),
    0
  );
  const avgViews =
    validData.length > 0 ? Math.round(totalViews / validData.length) : 0;
  const maxViews =
    validData.length > 0
      ? Math.max(...validData.map((item) => item.views || 0))
      : 0;
  const trend =
    validData.length > 1 && validData[0]?.views > 0
      ? ((validData[validData.length - 1].views - validData[0].views) /
          validData[0].views) *
        100
      : 0;

  return (
    <Card className="overflow-hidden">
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center gap-2">
          <div className="p-2 rounded-lg bg-green-50 dark:bg-green-900/20">
            <TrendingUp className="h-5 w-5 text-green-600 dark:text-green-400" />
          </div>
          {t("charts.viewsOverTime.title")}
        </CardTitle>
        <CardDescription>
          {t("charts.viewsOverTime.description")}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="relative">
          <ResponsiveContainer width="100%" height={320}>
            <AreaChart
              data={validData}
              margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
            >
              <defs>
                <linearGradient id="viewsGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop
                    offset="0%"
                    stopColor={CHART_COLORS.primary}
                    stopOpacity={0.4}
                  />
                  <stop
                    offset="100%"
                    stopColor={CHART_COLORS.primary}
                    stopOpacity={0.1}
                  />
                </linearGradient>
                <linearGradient
                  id="visitorsGradient"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop
                    offset="0%"
                    stopColor={CHART_COLORS.secondary}
                    stopOpacity={0.4}
                  />
                  <stop
                    offset="100%"
                    stopColor={CHART_COLORS.secondary}
                    stopOpacity={0.1}
                  />
                </linearGradient>
              </defs>
              <CartesianGrid
                strokeDasharray="3 3"
                className="stroke-muted/30"
                vertical={false}
              />
              <XAxis
                dataKey="date"
                className="text-xs fill-muted-foreground"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12 }}
                tickFormatter={(value) =>
                  new Date(value).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                  })
                }
              />
              <YAxis
                className="text-xs fill-muted-foreground"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12 }}
              />
              <Tooltip
                content={({ active, payload, label }) => {
                  if (active && payload && payload.length) {
                    return (
                      <div className="rounded-xl border bg-background/95 backdrop-blur-sm p-4 shadow-xl border-border/50">
                        <div className="mb-3">
                          <p className="font-semibold text-foreground">
                            {new Date(label).toLocaleDateString("en-US", {
                              weekday: "long",
                              month: "long",
                              day: "numeric",
                            })}
                          </p>
                        </div>
                        <div className="space-y-2">
                          {payload.map((entry, index) => (
                            <div
                              key={index}
                              className="flex items-center justify-between gap-4"
                            >
                              <div className="flex items-center gap-2">
                                <div
                                  className="w-3 h-3 rounded-full shadow-sm"
                                  style={{ backgroundColor: entry.color }}
                                />
                                <span className="text-sm text-muted-foreground">
                                  {entry.name}
                                </span>
                              </div>
                              <span className="font-medium text-foreground">
                                {entry.value?.toLocaleString()}
                              </span>
                            </div>
                          ))}
                        </div>
                        {payload.length === 2 &&
                          payload[0].value &&
                          payload[1].value && (
                            <div className="mt-3 pt-2 border-t border-border/50">
                              <div className="flex items-center justify-between text-xs">
                                <span className="text-muted-foreground">
                                  {t("charts.viewsOverTime.conversionRate")}
                                </span>
                                <span className="font-medium text-foreground">
                                  {(
                                    (Number(payload[1].value) /
                                      Number(payload[0].value)) *
                                    100
                                  ).toFixed(1)}
                                  %
                                </span>
                              </div>
                            </div>
                          )}
                      </div>
                    );
                  }
                  return null;
                }}
              />
              <Area
                type="monotone"
                dataKey="views"
                stroke={CHART_COLORS.primary}
                fill="url(#viewsGradient)"
                strokeWidth={2}
                name={t("charts.viewsOverTime.totalViews")}
                dot={false}
                activeDot={{
                  r: 4,
                  stroke: CHART_COLORS.primary,
                  strokeWidth: 2,
                  fill: "white",
                }}
              />
              <Area
                type="monotone"
                dataKey="uniqueVisitors"
                stroke={CHART_COLORS.secondary}
                fill="url(#visitorsGradient)"
                strokeWidth={2}
                name={t("charts.viewsOverTime.uniqueVisitors")}
                dot={false}
                activeDot={{
                  r: 4,
                  stroke: CHART_COLORS.secondary,
                  strokeWidth: 2,
                  fill: "white",
                }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Summary Stats with validation */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 pt-4 border-t border-border/50">
          <div className="text-center p-3 rounded-lg bg-card/50 border border-border/50">
            <p className="text-lg font-semibold text-foreground">
              {totalViews.toLocaleString()}
            </p>
            <p className="text-xs text-muted-foreground">
              {t("charts.viewsOverTime.totalViews")}
            </p>
          </div>
          <div className="text-center p-3 rounded-lg bg-card/50 border border-border/50">
            <p className="text-lg font-semibold text-foreground">
              {totalUniqueVisitors.toLocaleString()}
            </p>
            <p className="text-xs text-muted-foreground">
              {t("charts.viewsOverTime.uniqueVisitors")}
            </p>
          </div>
          <div className="text-center p-3 rounded-lg bg-card/50 border border-border/50">
            <p className="text-lg font-semibold text-foreground">
              {avgViews.toLocaleString()}
            </p>
            <p className="text-xs text-muted-foreground">
              {t("charts.viewsOverTime.avgDailyViews")}
            </p>
          </div>
          <div className="text-center p-3 rounded-lg bg-card/50 border border-border/50">
            <p className="text-lg font-semibold text-foreground">
              {maxViews.toLocaleString()}
            </p>
            <p className="text-xs text-muted-foreground">
              {t("charts.viewsOverTime.peakDay")}
            </p>
          </div>
          <div className="text-center p-3 rounded-lg bg-card/50 border border-border/50">
            <div className="flex items-center justify-center gap-1">
              <p
                className={`text-lg font-semibold ${
                  trend >= 0
                    ? "text-green-600 dark:text-green-400"
                    : "text-red-600 dark:text-red-400"
                }`}
              >
                {isNaN(trend) ? "0" : trend >= 0 ? "+" : ""}
                {isNaN(trend) ? "0" : trend.toFixed(1)}%
              </p>
              <TrendingUp
                className={`h-3 w-3 ${
                  trend >= 0
                    ? "text-green-600 dark:text-green-400 rotate-0"
                    : "text-red-600 dark:text-red-400 rotate-180"
                }`}
              />
            </div>
            <p className="text-xs text-muted-foreground">
              {t("charts.viewsOverTime.periodTrend")}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Geographic Distribution Chart
interface GeographicDistributionProps {
  data: Array<{ country: string; views: number; percentage: number }>;
  isLoading?: boolean;
}

export function GeographicDistributionChart({
  data,
  isLoading,
}: GeographicDistributionProps) {
  const { t } = useTranslation("dashboard");

  if (isLoading) {
    return (
      <Card className="overflow-hidden">
        <CardHeader className="pb-4">
          <div className="flex items-center gap-2">
            <Skeleton className="h-8 w-8 rounded-lg" />
            <div className="space-y-2">
              <Skeleton className="h-5 w-40" />
              <Skeleton className="h-4 w-60" />
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Chart skeleton */}
            <div className="relative">
              <Skeleton className="h-60 w-full rounded-lg" />
            </div>
            {/* Legend skeleton */}
            <div className="space-y-3">
              <div className="flex items-center justify-between mb-4">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-5 w-16 rounded-full" />
              </div>
              <div className="space-y-3">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between p-3 rounded-lg border border-border/50"
                  >
                    <div className="flex items-center gap-3">
                      <Skeleton className="w-4 h-4 rounded-full" />
                      <div className="space-y-1">
                        <Skeleton className="h-4 w-20" />
                        <Skeleton className="h-3 w-12" />
                      </div>
                    </div>
                    <div className="text-right">
                      <Skeleton className="h-5 w-12 rounded mb-1" />
                      <Skeleton className="h-3 w-8" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Show empty state if no data (but not loading)
  if (!Array.isArray(data) || data.length === 0) {
    return (
      <Card className="overflow-hidden">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2">
            <div className="p-2 rounded-lg bg-blue-50 dark:bg-blue-900/20">
              <Globe className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            {t("charts.geographicDistribution.title")}
          </CardTitle>
          <CardDescription>
            {t("charts.geographicDistribution.description")}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex items-center justify-center h-64">
          <div className="text-center">
            <Globe className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
            <p className="text-muted-foreground">
              {t("charts.geographicDistribution.noData")}
            </p>
            <p className="text-sm text-muted-foreground">
              {t("charts.geographicDistribution.noDataDescription")}
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="overflow-hidden">
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center gap-2">
          <div className="p-2 rounded-lg bg-blue-50 dark:bg-blue-900/20">
            <Globe className="h-5 w-5 text-blue-600 dark:text-blue-400" />
          </div>
          {t("charts.geographicDistribution.title")}
        </CardTitle>
        <CardDescription>
          {t("charts.geographicDistribution.description")}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Enhanced Pie Chart */}
          <div className="relative">
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <defs>
                  {PIE_COLORS.map((color, index) => (
                    <linearGradient
                      key={`gradient-${index}`}
                      id={`gradient-${index}`}
                      x1="0%"
                      y1="0%"
                      x2="100%"
                      y2="100%"
                    >
                      <stop offset="0%" stopColor={color} stopOpacity={0.8} />
                      <stop offset="100%" stopColor={color} stopOpacity={0.6} />
                    </linearGradient>
                  ))}
                </defs>
                <Pie
                  data={data.slice(0, 5)}
                  cx="50%"
                  cy="50%"
                  innerRadius={65}
                  outerRadius={125}
                  paddingAngle={2}
                  dataKey="views"
                  stroke="hsl(var(--background))"
                  strokeWidth={2}
                >
                  {data.slice(0, 5).map((_, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={`url(#gradient-${index})`}
                      className="hover:opacity-80 transition-opacity duration-200 cursor-pointer drop-shadow-sm"
                    />
                  ))}
                </Pie>
                <Tooltip
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      const data = payload[0].payload;
                      return (
                        <div className="rounded-xl border bg-background/95 backdrop-blur-sm p-4 shadow-xl border-border/50">
                          <div className="flex items-center gap-2 mb-2">
                            <div
                              className="w-3 h-3 rounded-full shadow-sm"
                              style={{
                                backgroundColor:
                                  payload[0].payload.fill || PIE_COLORS[0],
                              }}
                            />
                            <p className="font-semibold text-foreground">
                              {data.country}
                            </p>
                          </div>
                          <div className="space-y-1">
                            <p className="text-sm text-muted-foreground">
                              <span className="font-medium text-foreground">
                                {data.views.toLocaleString()}
                              </span>{" "}
                              views
                            </p>
                            <p className="text-sm text-muted-foreground">
                              <span className="font-medium text-foreground">
                                {data.percentage}%
                              </span>{" "}
                              of total traffic
                            </p>
                          </div>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
            {/* Center Stats */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="text-center">
                <p className="text-2xl font-bold text-foreground">
                  {data
                    .reduce((sum, country) => sum + country.views, 0)
                    .toLocaleString()}
                </p>
                <p className="text-sm text-muted-foreground">Total Views</p>
              </div>
            </div>
          </div>

          {/* Enhanced Legend */}
          <div className="space-y-3">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-medium text-sm text-muted-foreground">
                {t("charts.geographicDistribution.topCountries")}
              </h4>
              <Badge variant="secondary" className="text-xs">
                {data.length} {t("charts.geographicDistribution.countries")}
              </Badge>
            </div>
            <div className="space-y-3 max-h-48 overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-muted scrollbar-track-transparent">
              {data.slice(0, 8).map((country, index) => (
                <div
                  key={country.country}
                  className="group flex items-center justify-between p-3 rounded-lg border border-border/50 hover:border-border hover:shadow-sm transition-all duration-200 bg-card/50 hover:bg-card"
                >
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <div
                        className="w-4 h-4 rounded-full shadow-sm ring-2 ring-background"
                        style={{
                          backgroundColor:
                            index < 5
                              ? PIE_COLORS[index % PIE_COLORS.length]
                              : "#94a3b8",
                        }}
                      />
                      {index < 3 && (
                        <div className="absolute -top-1 -right-1 w-2 h-2 bg-yellow-400 rounded-full border border-background"></div>
                      )}
                    </div>
                    <div>
                      <span className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">
                        {country.country}
                      </span>
                      <p className="text-xs text-muted-foreground">
                        {t("charts.geographicDistribution.rank", {
                          rank: index + 1,
                        })}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center gap-2">
                      <Badge
                        variant={index < 3 ? "default" : "outline"}
                        className={`text-xs font-medium ${
                          index < 3
                            ? "bg-primary/10 text-primary border-primary/20"
                            : ""
                        }`}
                      >
                        {country.views.toLocaleString()}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {t("charts.geographicDistribution.share", {
                        percentage: country.percentage,
                      })}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            {data.length > 8 && (
              <div className="pt-2 border-t border-border/50">
                <p className="text-xs text-muted-foreground text-center">
                  {t("charts.geographicDistribution.moreCountries", {
                    count: data.length - 8,
                  })}
                </p>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Device Usage Chart
interface DeviceUsageProps {
  data: Array<{ device: string; count: number; percentage: number }>;
  isLoading?: boolean;
}

export function DeviceUsageChart({ data, isLoading }: DeviceUsageProps) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-40" />
          <Skeleton className="h-4 w-60" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-64 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Monitor className="h-5 w-5" />
          Device Usage
        </CardTitle>
        <CardDescription>Breakdown of viewer devices</CardDescription>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={data} layout="horizontal">
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis type="number" className="text-xs fill-muted-foreground" />
            <YAxis
              type="category"
              dataKey="device"
              className="text-xs fill-muted-foreground"
              width={80}
            />
            <Tooltip
              content={({ active, payload, label }) => {
                if (active && payload && payload.length) {
                  const data = payload[0].payload;
                  return (
                    <div className="rounded-lg border bg-background p-3 shadow-md">
                      <p className="font-medium capitalize">{label}</p>
                      <p className="text-sm text-muted-foreground">
                        {data.count} views ({data.percentage}%)
                      </p>
                    </div>
                  );
                }
                return null;
              }}
            />
            <Bar
              dataKey="count"
              fill={CHART_COLORS.primary}
              radius={[0, 4, 4, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}

// Engagement Metrics Chart
interface EngagementMetricsProps {
  data: Array<{
    name: string;
    avgViewTime: number;
    totalViews: number;
    bounceRate: number;
  }>;
  isLoading?: boolean;
}

export function EngagementMetricsChart({
  data,
  isLoading,
}: EngagementMetricsProps) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-40" />
          <Skeleton className="h-4 w-60" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-64 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock className="h-5 w-5" />
          Preview Engagement
        </CardTitle>
        <CardDescription>
          Average view time vs total views for top previews
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis
              dataKey="name"
              className="text-xs fill-muted-foreground"
              interval={0}
              angle={-45}
              textAnchor="end"
              height={80}
            />
            <YAxis yAxisId="left" className="text-xs fill-muted-foreground" />
            <YAxis
              yAxisId="right"
              orientation="right"
              className="text-xs fill-muted-foreground"
            />
            <Tooltip
              content={({ active, payload, label }) => {
                if (active && payload && payload.length) {
                  return (
                    <div className="rounded-lg border bg-background p-3 shadow-md">
                      <p className="font-medium">{label}</p>
                      {payload.map((entry, index) => (
                        <p
                          key={index}
                          className="text-sm"
                          style={{ color: entry.color }}
                        >
                          {entry.name}:{" "}
                          {entry.name === "Avg View Time"
                            ? `${entry.value}s`
                            : entry.value}
                        </p>
                      ))}
                    </div>
                  );
                }
                return null;
              }}
            />
            <Bar
              yAxisId="left"
              dataKey="totalViews"
              fill={CHART_COLORS.primary}
              name="Total Views"
            />
            <Bar
              yAxisId="right"
              dataKey="avgViewTime"
              fill={CHART_COLORS.secondary}
              name="Avg View Time"
            />
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}

// Hourly Activity Chart
interface HourlyActivityProps {
  data: Array<{ hour: number; views: number }>;
  isLoading?: boolean;
}

export function HourlyActivityChart({ data, isLoading }: HourlyActivityProps) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-40" />
          <Skeleton className="h-4 w-60" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-64 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Calendar className="h-5 w-5" />
          Activity by Hour
        </CardTitle>
        <CardDescription>
          Viewer activity throughout the day (24-hour format)
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis
              dataKey="hour"
              className="text-xs fill-muted-foreground"
              tickFormatter={(value) => `${value}:00`}
            />
            <YAxis className="text-xs fill-muted-foreground" />
            <Tooltip
              content={({ active, payload, label }) => {
                if (active && payload && payload.length) {
                  return (
                    <div className="rounded-lg border bg-background p-3 shadow-md">
                      <p className="font-medium">{label}:00</p>
                      <p className="text-sm text-muted-foreground">
                        {payload[0].value} views
                      </p>
                    </div>
                  );
                }
                return null;
              }}
            />
            <Line
              type="monotone"
              dataKey="views"
              stroke={CHART_COLORS.primary}
              strokeWidth={2}
              dot={{ fill: CHART_COLORS.primary, strokeWidth: 2, r: 4 }}
              activeDot={{ r: 6, stroke: CHART_COLORS.primary }}
            />
          </LineChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
